import { useCallback } from 'react'
import { useAuthStore } from '@/shared/state/authstore'
import { useChatUiStore } from '@/entities/chat'

const DOCK_STORAGE_PREFIX = 'neztrack-chat-dock'

export const DOCK_MIN_WIDTH = 320
export const DOCK_MIN_HEIGHT = 360
export const DOCK_DEFAULT_WIDTH = 380
export const DOCK_DEFAULT_HEIGHT = 560
const DOCK_VIEWPORT_MARGIN = 24

export type DockSize = { width: number; height: number }
export type PersistedDock = { open: boolean; selectedId: number | null } & DockSize

export function clampDockSize(size: DockSize): DockSize {
  const maxWidth = Math.max(DOCK_MIN_WIDTH, window.innerWidth - DOCK_VIEWPORT_MARGIN)
  const maxHeight = Math.max(DOCK_MIN_HEIGHT, window.innerHeight - DOCK_VIEWPORT_MARGIN)
  return {
    width: Math.min(Math.max(Math.round(size.width), DOCK_MIN_WIDTH), maxWidth),
    height: Math.min(Math.max(Math.round(size.height), DOCK_MIN_HEIGHT), maxHeight),
  }
}

export function dockStorageKey(userId: number | null, accountId: number | null): string | null {
  if (userId === null || accountId === null) return null
  return `${DOCK_STORAGE_PREFIX}:${userId}:${accountId}`
}

export function readPersistedDock(key: string | null): PersistedDock {
  const fallback: PersistedDock = {
    open: true,
    selectedId: null,
    width: DOCK_DEFAULT_WIDTH,
    height: DOCK_DEFAULT_HEIGHT,
  }
  if (!key) return fallback
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as Partial<PersistedDock>
    const size = clampDockSize({
      width: typeof parsed.width === 'number' ? parsed.width : fallback.width,
      height: typeof parsed.height === 'number' ? parsed.height : fallback.height,
    })
    return {
      open: typeof parsed.open === 'boolean' ? parsed.open : fallback.open,
      selectedId: typeof parsed.selectedId === 'number' ? parsed.selectedId : null,
      ...size,
    }
  } catch {
    return fallback
  }
}

export function persistDockPatch(key: string | null, patch: Partial<PersistedDock>): void {
  if (!key) return
  const current = readPersistedDock(key)
  window.localStorage.setItem(key, JSON.stringify({ ...current, ...patch }))
}

function requestComposerFocusIfOpeningOntoThread(wasOpen: boolean): void {
  if (wasOpen) return
  const state = useChatUiStore.getState()
  if (!state.dockOpen || state.selectedConversationId == null) return
  state.setComposerFocusRequested(true)
}

export function useChatDockControls() {
  const userId = useAuthStore((state) => state.userId)
  const accountId = useAuthStore((state) => state.accountId)
  const key = dockStorageKey(userId, accountId)

  const dockOpen = useChatUiStore((state) => state.dockOpen)
  const selectedConversationId = useChatUiStore((state) => state.selectedConversationId)

  const setDockOpen = useCallback(
    (open: boolean) => {
      const wasOpen = useChatUiStore.getState().dockOpen
      useChatUiStore.getState().setDockOpen(open)
      persistDockPatch(key, { open })
      requestComposerFocusIfOpeningOntoThread(wasOpen)
    },
    [key],
  )

  const toggleDock = useCallback(() => {
    const wasOpen = useChatUiStore.getState().dockOpen
    useChatUiStore.getState().toggleDock()
    persistDockPatch(key, { open: useChatUiStore.getState().dockOpen })
    requestComposerFocusIfOpeningOntoThread(wasOpen)
  }, [key])

  const selectConversation = useCallback(
    (id: number | null) => {
      useChatUiStore.getState().setSelectedConversation(id)
      persistDockPatch(key, { selectedId: id })
      if (id != null) useChatUiStore.getState().setComposerFocusRequested(true)
    },
    [key],
  )

  return { dockOpen, selectedConversationId, setDockOpen, toggleDock, selectConversation }
}
